import BaseService from './BaseService';

export default class PbxService extends BaseService {
  async assignPbxLine({
    pbxLineId,
    orgId,
    targetId,
    targetType,
  }: {
    pbxLineId: string;
    orgId: string;
    targetId: string;
    targetType: string;
  }) {
    return this.host.api.pbx.assignPbxLine({
      pbxLineId,
      orgId,
      targetId,
      targetType,
    });
  }

  async retrieveNurseCallStatus({ orgId }: { orgId: string }) {
    return this.host.api.pbx.retrieveNurseCallStatus({
      orgId,
    });
  }

  async batchUnAssignPbxLines({
    lineType,
    orgId,
    pbxLineIds,
    targetIds,
  }: {
    lineType: string;
    orgId: string;
    pbxLineIds: string[];
    targetIds?: string[];
  }) {
    return this.host.api.pbx.batchUnAssignPbxLines({
      lineType,
      orgId,
      pbxLineIds,
      targetIds,
    });
  }

  async getPbxLineDetails({ orgId, pbxLineId }: { orgId: string; pbxLineId: string }) {
    return this.host.api.pbx.getPbxLineDetails({
      pbxLineId,
      orgId,
    });
  }

  async unAssignPbxLine({
    lineType,
    orgId,
    pbxLineId,
    targetIds,
  }: {
    lineType: string;
    orgId: string;
    pbxLineId: string;
    targetIds?: string[];
  }) {
    return this.host.api.pbx.unAssignPbxLine({
      lineType,
      orgId,
      pbxLineId,
      targetIds,
    });
  }

  async batchAssignUsersToNCLines({ targetIds, orgId }: { targetIds: string[]; orgId: string }) {
    return this.host.api.pbx.batchAssignUsersToNCLines({
      targetIds,
      orgId,
    });
  }

  async getPbxLines({
    page,
    orgId,
    size,
    targetType,
    search,
    isAssigned,
    sortField,
    sortDirection,
  }: {
    orgId: string;
    page: number;
    size: number;
    targetType: 'User' | 'Role' | 'NurseCall';
    search?: string;
    isAssigned?: boolean;
    sortField?: 'displayName' | 'extensionNumber';
    sortDirection?: 'asc' | 'desc';
  }) {
    return this.host.api.pbx.getPbxLines({
      page,
      orgId,
      size,
      targetType,
      search,
      isAssigned,
      sortField,
      sortDirection,
    });
  }

  async getPbxLinesByExtension({
    orgToken,
    extensionNumber,
    targetType,
  }: {
    orgToken: string;
    extensionNumber: string;
    targetType: string;
  }) {
    return this.host.api.pbx.getPbxLinesByExtension({
      orgToken,
      extensionNumber,
      targetType,
    });
  }

  async getPbxDirectory({
    orgToken,
    page,
    size,
    sort,
    search,
  }: {
    orgToken: string;
    page?: number;
    size?: number;
    sort?: string;
    search?: string;
  }) {
    return this.host.api.pbx.getPbxDirectory({ orgToken, page, size, sort, search });
  }

  async createPbxDirectoryEntry({
    orgToken,
    entry,
  }: {
    orgToken: string;
    entry: {
      displayName: string;
      phoneNumber: string;
      location?: string;
      department?: string;
      title?: string;
    };
  }) {
    return this.host.api.pbx.createPbxDirectoryEntry({ orgToken, entry });
  }

  async updatePbxDirectoryEntry({
    orgToken,
    id,
    entry,
  }: {
    orgToken: string;
    id: string;
    entry: {
      displayName: string;
      phoneNumber: string;
      location?: string;
      department?: string;
      title?: string;
      organizationId?: number;
    };
  }) {
    return this.host.api.pbx.updatePbxDirectoryEntry({ orgToken, id, entry });
  }

  async deletePbxDirectoryEntry({ id }: { id: string }) {
    return this.host.api.pbx.deletePbxDirectoryEntry({ id });
  }

  async batchDeletePbxDirectoryEntries({ orgToken, ids }: { orgToken: string; ids: number[] }) {
    return this.host.api.pbx.batchDeletePbxDirectoryEntries({ orgToken, ids });
  }

  async uploadPbxDirectoryViaCsv({
    currentOrganizationId,
    file,
  }: {
    currentOrganizationId: string;
    file: File;
  }) {
    const headers = this.httpClient.getAuthHeaders();
    headers['TT-X-Organization-Key'] = currentOrganizationId;

    return this.host.api.pbx.uploadPbxDirectoryViaCsv({
      headers,
      file,
    });
  }

  async uploadPbxLinesViaCsv({
    currentOrganizationId,
    email,
    file,
    systemConfigurationId,
  }: {
    currentOrganizationId: string;
    email: string;
    file: File;
    systemConfigurationId: string;
  }) {
    const headers = this.httpClient.getAuthHeaders();
    headers['TT-X-Organization-Key'] = currentOrganizationId;

    return this.host.api.pbx.uploadPbxLinesViaCsv({
      systemConfigurationId,
      email,
      headers,
      file,
    });
  }

  async getPbxSystems({
    currentOrganizationId,
    containNurseCall,
  }: {
    currentOrganizationId: string;
    containNurseCall: boolean;
  }) {
    return this.host.api.pbx.getPbxSystems({ currentOrganizationId, containNurseCall });
  }
}
